import { create } from 'zustand';

/**
 * Tracks embedded custom apps (AppEmbed iframes) that have announced a newer
 * build of themselves via the `rumio:update-available` postMessage (apps
 * platform contract v1.13). The global UpdateAvailableBanner reads this store
 * and renders one "A new version of <app> is available" entry per pending
 * update, alongside the host shell's own deploy-freshness banner.
 *
 * Entries are ephemeral runtime signals (no persistence): AppEmbed sets one
 * when its iframe reports an update and clears it on unmount. The `reload`
 * closure is owned by the AppEmbed instance — it posts `rumio:reload` back to
 * that specific iframe so the app can hard-refresh itself (clearing its own
 * service worker + caches same-origin, which the host can't do cross-origin).
 */
export interface AppUpdateEntry {
  /** Rumio-side id / client_id of the app, used as the store key. */
  appId: string;
  /** Human label for the banner (the embed's title / mount label). */
  name: string;
  /** Tell the app's iframe to hard-reload (posts `rumio:reload`). */
  reload: () => void;
}

interface AppUpdateState {
  updates: Record<string, AppUpdateEntry>;
  setUpdate: (entry: AppUpdateEntry) => void;
  clearUpdate: (appId: string) => void;
}

export const useAppUpdateStore = create<AppUpdateState>((set) => ({
  updates: {},
  setUpdate: (entry) =>
    set((state) => ({ updates: { ...state.updates, [entry.appId]: entry } })),
  clearUpdate: (appId) =>
    set((state) => {
      if (!(appId in state.updates)) return state;
      const next = { ...state.updates };
      delete next[appId];
      return { updates: next };
    }),
}));
